
import Page from '@root/common/base/Page';
import Meta from '@root/common/utils/Meta';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import Episode from '@root/modules/podcast/components/fragments/Episode.vue';
import PianoCustomerStateHandler from '@piano/components/PianoCustomerStateHandler.vue';
import PodcastPaywall from '@root/modules/podcast/components/PodcastPaywall.vue';
import getFrontPageAdsSettings from '@root/modules/frontpage/utils/getFrontPageAdsSettings';
import isMobileView from '@root/common/utils/isMobileView';
import { Podcast } from '@root/modules/podcast/types/podcast';
import getPodcastWithHref from '@root/modules/podcast/utils/getPodcastWithHref';

interface Data {
  isMobileView: boolean;
}

interface Computed {
  customerPlaylist: Podcast[];
  customerToken: string | null;
  items: Podcast[];
  showPaywall: boolean;
  showPlayer: boolean;
}

interface Methods {
  getCustomerPlaylist: () => void;
  isBook: (episode: Podcast) => boolean;
}

export default Page.extend<Data, Methods, Computed, unknown>({
  components: {
    AdsLayout,
    Episode,
    PianoCustomerStateHandler,
    PodcastPaywall,
  },
  data() {
    return {
      isMobileView: false,
    };
  },
  computed: {
    customerPlaylist() {
      return this.$store.state.podcast.customerPlaylist || [];
    },
    customerToken() {
      return this.$store.state.piano.token;
    },
    items() {
      let data = [...this.customerPlaylist];

      if (this.customerPlaylist.length) {
        const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
        const podcastsWithHref = data.map((podcast: Podcast) => {
          const { domain } = this.$channelConfig('settings');
          return getPodcastWithHref({ podcast, domain, fakeDomainEnabled });
        });

        data = podcastsWithHref;
      }

      return data;
    },
    showPaywall() {
      return this.$store.state.podcast.showPaywall;
    },
    showPlayer() {
      return Boolean(this.$player?.state.currentlyPlaying.audioId);
    },
  },
  watch: {
    async customerToken() {
      if (this.customerToken) {
        await this.$store.dispatch('podcast/setCustomerToken', this.customerToken);
        await this.getCustomerPlaylist();
      }
    },
  },
  methods: {
    async getCustomerPlaylist() {
      await this.$store.dispatch('podcast/setCustomerPlaylist');
    },
    isBook(episode) {
      return episode.primaryCategory.parentCategory?.parentCategory?.id === 96603927;
    },
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();

    next();
  },
  mounted() {
    this.restoreScroll();
    this.isMobileView = isMobileView();
    this.setPageAdsSettings(getFrontPageAdsSettings({ $channelConfig: this.$channelConfig }));
  },
  head() {
    const meta = new Meta(this.$channelConfig('meta').base, this.$route.path);

    meta.title = meta.ogTitle = this.$t('podcast.meta.playlist.title') as string;
    meta.description = meta.ogDescription = this.$t('podcast.meta.playlist.description') as string;

    return {
      title: meta.title,
      meta: meta.data,
      link: meta.link,
    };
  },
});
